import type { ActionResponse } from '@acme/api';
import {
  ActionService,
  DefinitionActionKindEnum,
  DefinitionActionSubKindEnum,
} from '@acme/api';
import {
  ProvideWalletsModal,
  RejectModal,
  ReworkModal,
  TransferFiatModal,
  UploadDocumentModal,
  findAction,
} from '@acme/features';
import { useModal } from '@acme/foundation';
import { AddWalletAddressesForm } from '@features/addWalletAddressesForm';
import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';

type ApplicationLike = {
  code?: string | null;
  name?: string | null;
} | null;

type Params = {
  id: string | undefined;
  navigate: NavigateFunction;
  application: ApplicationLike;
  actions: ActionResponse[] | null;
  onConfirmClicked: () => void;
  onDeleteClicked: () => void;
  onRejectRequested: (p: { actionId: string; comment: string }) => void;
  onReworkRequested: (p: { actionId: string; comment: string }) => void;
  onSignRequested: (p: { actionId: string }) => void;
  onApplicationOpened: (p: { id: string }) => void;
  onActionResultShown: (p: {
    isError: boolean;
    actionName?: string | null;
  }) => void;
};

export function useApplicationActionHandlers({
  id,
  navigate,
  application,
  actions,
  onConfirmClicked,
  onDeleteClicked,
  onRejectRequested,
  onReworkRequested,
  onSignRequested,
  onApplicationOpened,
  onActionResultShown,
}: Params) {
  const { openModal, closeModal } = useModal();

  const applicationTitle = application
    ? `${application.code} - ${application.name}`
    : 'заявка';

  const handleReject = useCallback(() => {
    const action = findAction(actions, DefinitionActionKindEnum.Reject);
    if (!action?.id) return;
    const actionId = action.id;
    const actionName = action.name ?? null;
    openModal({
      title: 'Отклонить заявку',
      content: (
        <RejectModal
          onConfirm={(comment) => {
            onRejectRequested({ actionId, comment });
            closeModal();
            onActionResultShown({ isError: false, actionName });
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  }, [actions, openModal, closeModal, onRejectRequested, onActionResultShown]);

  const handleRework = useCallback(() => {
    const action = findAction(actions, DefinitionActionKindEnum.Rework);
    if (!action?.id) return;
    const actionId = action.id;
    openModal({
      title: 'Исправить заявку',
      content: (
        <ReworkModal
          onConfirm={(comment) => {
            onReworkRequested({ actionId, comment });
            closeModal();
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  }, [actions, openModal, closeModal, onReworkRequested]);

  const handleEditDraft = useCallback(() => {
    if (id) navigate(`/application/${id}/edit`);
  }, [id, navigate]);

  const handleSendOffer = useCallback(() => {
    if (id) navigate(`/application/${id}/send-offer`);
  }, [id, navigate]);

  const handleCounterpartyOffer = useCallback(() => {
    if (id) navigate(`/application/${id}/counterparty-offer`);
  }, [id, navigate]);

  const handleAgentInfo = useCallback(() => {
    if (id) navigate(`/application/${id}/agent-info`);
  }, [id, navigate]);

  const handleExchangeRates = useCallback(() => {
    if (id) navigate(`/application/${id}/liquidity-providers`);
  }, [id, navigate]);

  const handleFormWalletsWhichTransfer = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Form,
      DefinitionActionSubKindEnum.FormWalletsWhichTransfer
    );
    if (!action?.id) return;
    openModal({
      title: 'Адреса криптокошельков',
      content: (
        <AddWalletAddressesForm
          actionId={action.id}
          closeModal={() => {
            closeModal();
            if (id) onApplicationOpened({ id });
          }}
        />
      ),
      onClose: closeModal,
    });
  }, [actions, id, openModal, closeModal, onApplicationOpened]);

  const handleTransferFiat = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Form,
      DefinitionActionSubKindEnum.FormTransferFiat
    );
    if (!action?.id || !id) return;
    const actionId = action.id;
    openModal({
      title: 'Перевод в фиат',
      content: (
        <TransferFiatModal
          onConfirm={async ({ fiatAmount: fiatAmountValue, comment }) => {
            try {
              const res = await ActionService.postApiV1ActionFormTransferFiat({
                id: actionId,
                formData: { fiatAmount: fiatAmountValue, comment },
              });
              if (res?.isSuccess) {
                closeModal();
                onApplicationOpened({ id });
              }
            } catch {
              // ошибка обрабатывается глобально
            }
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  }, [actions, id, openModal, closeModal, onApplicationOpened]);

  const handleFormProvideWallets = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Form,
      DefinitionActionSubKindEnum.FormProvideWallets
    );
    if (!action?.id || !id) return;
    const actionId = action.id;
    openModal({
      title: 'Изменить адрес',
      content: (
        <ProvideWalletsModal
          onConfirm={async ({ walletAddress, comment }) => {
            const res = await ActionService.postApiV1ActionFormProvideWallets({
              id: actionId,
              formData: { walletAddress, comment },
            });
            if (res?.isSuccess) {
              closeModal();
              onApplicationOpened({ id });
            } else {
              throw new Error(res?.errorText ?? 'Не удалось сохранить');
            }
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  }, [actions, id, openModal, closeModal, onApplicationOpened]);

  const handleFormTransferProvideWallets = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Form,
      DefinitionActionSubKindEnum.FormTransferProvideWallets
    );
    if (!action?.id || !id) return;
    const actionId = action.id;
    openModal({
      title: action.name ?? 'Подтвердить тестовый платеж',
      content: (
        <ProvideWalletsModal
          onConfirm={async ({ walletAddress, comment }) => {
            const res = await ActionService.postApiV1ActionFormProvideWallets({
              id: actionId,
              formData: { walletAddress, comment },
            });
            if (res?.isSuccess) {
              closeModal();
              onApplicationOpened({ id });
            } else {
              throw new Error(res?.errorText ?? 'Не удалось сохранить');
            }
          }}
          onCancel={closeModal}
        />
      ),
      onClose: closeModal,
    });
  }, [actions, id, openModal, closeModal, onApplicationOpened]);

  const signContractAction =
    actions?.find(
      (a) =>
        a.kind === DefinitionActionKindEnum.Sign &&
        (a.subKind === null || a.subKind === undefined)
    ) ?? null;

  const handleSignContract = useCallback(() => {
    if (signContractAction?.id && id)
      navigate(`/application/${id}/sign/${signContractAction.id}`);
  }, [signContractAction, id, navigate]);

  const handleSignExchangeContract = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Sign,
      DefinitionActionSubKindEnum.SignExchangeContract
    );
    if (action?.id && id) navigate(`/application/${id}/sign/${action.id}`);
  }, [actions, id, navigate]);

  const handleSignExchangeAct = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Sign,
      DefinitionActionSubKindEnum.SignExchangeAct
    );
    if (action?.id) onSignRequested({ actionId: action.id });
  }, [actions, onSignRequested]);

  const handleSignTransferContract = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Sign,
      DefinitionActionSubKindEnum.SignTransferContract
    );
    if (action?.id) onSignRequested({ actionId: action.id });
  }, [actions, onSignRequested]);

  const handleSignTransferAct = useCallback(() => {
    const action = findAction(
      actions,
      DefinitionActionKindEnum.Sign,
      DefinitionActionSubKindEnum.SignTransferAct
    );
    if (action?.id) onSignRequested({ actionId: action.id });
  }, [actions, onSignRequested]);

  const navigateToUpload = useCallback(
    (subKind: DefinitionActionSubKindEnum) => () => {
      const action = findAction(
        actions,
        DefinitionActionKindEnum.Upload,
        subKind
      );
      if (action?.id && id) navigate(`/application/${id}/upload/${action.id}`);
    },
    [actions, id, navigate]
  );

  const handleUploadClientPaymentDoc = navigateToUpload(
    DefinitionActionSubKindEnum.UploadClientPaymentDoc
  );

  const handleUploadAdminToProviderDoc = navigateToUpload(
    DefinitionActionSubKindEnum.UploadAdminToProviderDoc
  );

  const handleUploadTransferCommessionRecepotDoc = navigateToUpload(
    DefinitionActionSubKindEnum.UploadTransferCommessionRecepotDoc
  );

  const handleUploadTransferRecepotDoc = navigateToUpload(
    DefinitionActionSubKindEnum.UploadTransferRecepotDoc
  );

  const handleConfirmDealUpload = useCallback(
    (actionId?: string) => {
      if (!actionId || !id) return;
      openModal({
        title: 'Подтверждение проведения сделки',
        size: 'auto',
        content: (
          <UploadDocumentModal
            title="Загрузить документ"
            onConfirm={async (file) => {
              const res = await ActionService.postApiV1ActionUpload({
                id: actionId,
                formData: { files: [file] },
              });
              if (res?.isSuccess) {
                closeModal();
                onApplicationOpened({ id });
              } else {
                throw new Error(
                  res?.errorText ?? 'Не удалось отправить документ'
                );
              }
            }}
            onCancel={closeModal}
          />
        ),
        onClose: closeModal,
      });
    },
    [id, openModal, closeModal, onApplicationOpened]
  );

  return {
    applicationTitle,
    props: {
      applicationId: id,
      actions,
      confirm: {
        handler: onConfirmClicked,
        confirmModal: {
          title: 'Подтвердить действие?',
          content: `Вы уверены, что хотите подтвердить действие по заявке ${applicationTitle}?`,
        },
      },
      reject: { handler: handleReject },
      rework: {
        handler: handleRework,
        editDraft: { handler: handleEditDraft },
      },
      delete: {
        handler: onDeleteClicked,
        confirmModal: {
          title: 'Удалить заявку?',
          content: `Вы уверены, что хотите удалить заявку ${applicationTitle}? Это действие необратимо.`,
        },
      },
      form: {
        formEditApplication: { handler: handleSendOffer },
        formCounterpartyInfo: { handler: handleCounterpartyOffer },
        formAgentInfo: { handler: handleAgentInfo },
        formExchangeRates: { handler: handleExchangeRates },
        formWalletsWhichTransfer: { handler: handleFormWalletsWhichTransfer },
        formProvideWallets: { handler: handleFormProvideWallets },
        formTransferProvideWallets: {
          handler: handleFormTransferProvideWallets,
        },
        formTransferFiat: { handler: handleTransferFiat },
      },
      sign: {
        signContract: signContractAction
          ? { handler: () => handleSignContract() }
          : undefined,
        signExchangeContract: { handler: handleSignExchangeContract },
        signExchangeAct: { handler: handleSignExchangeAct },
        signTransferContract: { handler: handleSignTransferContract },
        signTransferAct: { handler: handleSignTransferAct },
      },
      upload: {
        uploadClientPaymentDoc: { handler: handleUploadClientPaymentDoc },
        uploadAdminToProviderDoc: { handler: handleUploadAdminToProviderDoc },
        uploadTransferCommessionRecepotDoc: {
          handler: handleUploadTransferCommessionRecepotDoc,
        },
        uploadTransferRecepotDoc: { handler: handleUploadTransferRecepotDoc },
      },
      confirmDealUpload: { handler: handleConfirmDealUpload },
    },
  };
}
